/**
 * What can be done to the record itself, as opposed to its document.
 *
 *   Reviewed, no changes — the owner looked and found nothing to change.
 *       The review clock resets and a review record is written; nobody is
 *       asked to acknowledge anything again. Half of all periodic reviews
 *       end this way, and without a button for it the only way to "review"
 *       a policy was to republish it.
 *   Retire — an administrator withdraws it, with a reason. Staff stop
 *       seeing it; the record and every version are kept, as HIPAA's six
 *       years require. Pending acknowledgements are cancelled.
 *   Withdraw from approval — the author pulls a submitted document back,
 *       with a reason. The approval is closed and KEPT, with any decisions
 *       already made; the document returns to draft. Until this existed the
 *       only way out of a chain was for an approver to "request changes",
 *       which reads as a rejection of something nobody rejected.
 *   Delete draft — a draft nobody has approved; the one thing here that
 *       really is gone afterwards.
 */

import { useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../auth/useAuth';
import { call, isCallFailure } from '../../lib/callable';
import { type Role } from '../../types/roles';
import { SUMMARY_MAX } from '../../types/fieldLimits';
import { policyCapabilities } from './policyCapabilities';
import type { PolicyDetail } from './usePolicy';
import styles from '../../shared/Page.module.css';
import up from './UploadPage.module.css';
import own from './DocumentSection.module.css';

const REASON_MAX = 2000;
const NOTES_MAX = 1000;
/** Mirror WITHDRAW_REASON_MIN in functions/src/policies/withdraw.ts. */
export const WITHDRAW_REASON_MIN = 10;

/** Which record actions this person may take on this policy. */
/**
 * What this person may do to this policy, from the one shared rule.
 *
 * This used to be five hand-written conditions that tested ownership with
 * `ownerId` alone and applied no role floor at all. Both halves were wrong:
 * somebody who CREATED a policy but is not its named owner was shown nothing,
 * and a viewer who happened to be the owner was shown buttons every callable
 * refuses. `policyCapabilities` is the same module the server uses.
 */
export function recordActions(
  policy: Pick<PolicyDetail, 'status' | 'revisionStatus' | 'ownerId' | 'createdBy' | 'ownerUserId'
    | 'revisionStartedBy' | 'coOwnerIds' | 'readOnly' | 'source' | 'submittedBy'>,
  roles: readonly Role[],
  uid: string | null,
) {
  const can = policyCapabilities(policy, { uid, roles });
  return {
    reviewed: can.markReviewed,
    retire: can.retire,
    deleteDraft: can.deleteDraft,
    release: can.publishNow,
    withdraw: can.withdraw,
  };
}

function useRecordMutation<TInput>(name: string, orgId: string | null, policyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => call<TInput, unknown>(name, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['policy', orgId, policyId] });
      void qc.invalidateQueries({ queryKey: ['policies', orgId] });
      void qc.invalidateQueries({ queryKey: ['policy-counts', orgId] });
      void qc.invalidateQueries({ queryKey: ['policy-versions', orgId, policyId] });
    },
  });
}

type Panel = 'reviewed' | 'retire' | 'delete' | 'withdraw' | 'release' | null;

export function RecordActions({ policy, onDone }: { policy: PolicyDetail; onDone: (message: string) => void }) {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const can = recordActions(policy, session?.roles ?? [], session?.uid ?? null);
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel>(null);
  const [text, setText] = useState('');
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  const reviewed = useRecordMutation<{ policyId: string; notes?: string }>('markReviewedNoChanges', orgId, policy.id);
  const retire = useRecordMutation<{ policyId: string; reason?: string }>('retirePolicy', orgId, policy.id);
  const remove = useRecordMutation<{ policyId: string }>('deletePolicyDraft', orgId, policy.id);
  const withdraw = useRecordMutation<{ policyId: string; reason: string }>('withdrawPolicyFromApproval', orgId, policy.id);
  const release = useRecordMutation<{ policyId: string }>('publishPolicy', orgId, policy.id);
  const busy = reviewed.isPending || retire.isPending || remove.isPending || withdraw.isPending || release.isPending;
  const error = reviewed.error ?? retire.error ?? remove.error ?? withdraw.error ?? release.error;

  if (!can.reviewed && !can.retire && !can.deleteDraft && !can.withdraw && !can.release) return null;

  function open(p: Panel) { setPanel(panel === p ? null : p); setText(''); }

  function doReviewed(e: FormEvent) {
    e.preventDefault();
    reviewed.mutate({ policyId: policy.id, ...(text.trim() ? { notes: text.trim() } : {}) }, {
      onSuccess: () => { setPanel(null); onDone('Recorded as reviewed with no changes. The next review date has moved on by one cycle.'); },
    });
  }

  function doRetire(e: FormEvent) {
    e.preventDefault();
    retire.mutate({ policyId: policy.id, ...(text.trim() ? { reason: text.trim() } : {}) }, {
      onSuccess: () => { setPanel(null); onDone('Retired. Staff no longer see it; the record and every version are kept.'); },
    });
  }

  function doWithdraw(e: FormEvent) {
    e.preventDefault();
    if (text.trim().length < WITHDRAW_REASON_MIN) return;
    withdraw.mutate({ policyId: policy.id, reason: text.trim() }, {
      onSuccess: () => { setPanel(null); onDone('Withdrawn from approval. It is a draft again; the approvers it was waiting on have been told. Submitting it again starts a new approval at the first step.'); },
    });
  }

  function doDelete() {
    remove.mutate({ policyId: policy.id }, { onSuccess: () => navigate('/policies') });
  }

  return (
    <section className={styles.section} aria-labelledby="record-actions-title">
      <h2 className={styles.sectionTitle} id="record-actions-title">Manage</h2>
      {error && (
        <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
          {isCallFailure(error) ? error.message : 'That could not be done.'}
        </div>
      )}
      <div className={styles.actions}>
        {can.reviewed && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'reviewed'} onClick={() => open('reviewed')}>Reviewed, no changes…</button>}
        {can.release && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'release'} onClick={() => open('release')}>Publish now…</button>}
        {can.withdraw && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'withdraw'} onClick={() => open('withdraw')}>Withdraw from approval…</button>}
        {can.retire && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'retire'} onClick={() => open('retire')}>Retire…</button>}
        {can.deleteDraft && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'delete'} onClick={() => open('delete')}>Delete draft…</button>}
      </div>

      {panel === 'reviewed' && (
        <form className={own.panel} onSubmit={doReviewed} aria-labelledby={id('rv-t')}>
          <h3 className={own.panelTitle} id={id('rv-t')}>Reviewed, no changes</h3>
          <p className={up.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            Records that you reviewed this policy today and found nothing to change. The next review date moves on by one cycle. Nobody is asked to acknowledge it again.
          </p>
          <label className={up.label} htmlFor={id('rv-n')}>Notes <span className={up.optional}>optional</span></label>
          <textarea id={id('rv-n')} className={up.textarea} rows={2} maxLength={NOTES_MAX} value={text} onChange={(e) => setText(e.target.value.slice(0, NOTES_MAX))} disabled={busy} />
          <div className={styles.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{reviewed.isPending ? 'Recording…' : 'Record the review'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </form>
      )}

      {panel === 'release' && (
        <div className={own.panel} role="group" aria-labelledby={id('rl-t')}>
          <h3 className={own.panelTitle} id={id('rl-t')}>Publish it now, ahead of its effective date?</h3>
          <p className={up.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            It has been approved and is waiting for its effective date, when it would be published on its own. Publishing now makes it what staff read
            today and asks them to acknowledge it. The approval already given stands.
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
              onClick={() => release.mutate({ policyId: policy.id }, { onSuccess: () => { setPanel(null); onDone('Published. Staff can read it now.'); } })}>
              {release.isPending ? 'Publishing…' : 'Publish now'}
            </button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Wait for the date</button>
          </div>
        </div>
      )}

      {panel === 'withdraw' && (
        <form className={own.panel} onSubmit={doWithdraw} aria-labelledby={id('wd-t')}>
          <h3 className={own.panelTitle} id={id('wd-t')}>Withdraw it from approval?</h3>
          <p className={up.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            It becomes a draft again and the people it is waiting on are told. The approval is closed, not deleted: its steps and any
            decisions already made stay in the history, marked Withdrawn. Submitting again starts a new approval at the first step,
            on whichever workflow you choose then. Decisions are not carried across.
          </p>
          <label className={up.label} htmlFor={id('wd-r')}>Reason</label>
          <textarea id={id('wd-r')} className={up.textarea} rows={2} required maxLength={SUMMARY_MAX} value={text} aria-describedby={id('wd-h')}
            onChange={(e) => setText(e.target.value.slice(0, SUMMARY_MAX))} disabled={busy} />
          <p className={up.hint} id={id('wd-h')}>The approvers see this, and it is kept on the record. At least {WITHDRAW_REASON_MIN} characters.</p>
          <div className={styles.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || text.trim().length < WITHDRAW_REASON_MIN}>{withdraw.isPending ? 'Withdrawing…' : 'Withdraw it'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Keep it in approval</button>
          </div>
        </form>
      )}

      {panel === 'retire' && (
        <form className={own.panel} onSubmit={doRetire} aria-labelledby={id('rt-t')}>
          <h3 className={own.panelTitle} id={id('rt-t')}>Retire this policy?</h3>
          <p className={up.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            Staff stop seeing it and any outstanding acknowledgements are cancelled. The record and every version stay on file for auditors; a new version in progress is discarded.
          </p>
          <label className={up.label} htmlFor={id('rt-r')}>Reason</label>
          <textarea id={id('rt-r')} className={up.textarea} rows={2} maxLength={REASON_MAX} value={text} onChange={(e) => setText(e.target.value.slice(0, REASON_MAX))} disabled={busy} />
          <div className={styles.actions} style={{ marginTop: 'var(--sp-3)' }}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{retire.isPending ? 'Retiring…' : 'Retire'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </form>
      )}

      {panel === 'delete' && (
        <div className={own.panel} role="group" aria-labelledby={id('dl-t')}>
          <h3 className={own.panelTitle} id={id('dl-t')}>Delete this draft?</h3>
          <p className={up.hint} style={{ marginBottom: 'var(--sp-3)' }}>
            A draft nobody has approved. This cannot be undone.
          </p>
          <div className={styles.actions}>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={doDelete}>{remove.isPending ? 'Deleting…' : 'Delete it'}</button>
            <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
